import { Injectable } from '@angular/core';
import { modifiedSetTimeout } from './utilities/ZoneUtils';
import { FLAGS, FlagsService } from './services/flags.service';

/**
 * This code is adapted from the WebRTC test suite:
 * https://github.com/webrtc/testrtc
 *
 * The microphone test functionality analyzes audio input to detect common issues
 * like clipping, low volume, and silence. It helps ensure proper microphone setup
 *
 * Updated to use AudioWorkletNode instead of the deprecated ScriptProcessorNode
 */

/**
 * Message sent from worklet to main thread when audio collection is complete
 */
interface AudioCompleteMessage {
  type: 'audioComplete';
  collectedAudio: Float32Array[][];
  collectedSampleCount: number;
}

/**
 * Command sent from main thread to worklet to stop collection
 */
interface StopCommandMessage {
  command: 'stop';
}

/**
 * Type for the MessageEvent received from the worklet
 */
interface WorkletMessageEvent extends MessageEvent {
  data: AudioCompleteMessage;
}

@Injectable({
  providedIn: 'root',
})
export class MicMuteDetectionService {
  // Audio processing configuration
  readonly inputChannelCount = 6;
  readonly outputChannelCount = 2;

  // Default values (used as fallbacks if flags are not set)
  private readonly defaultMaxAudioDataSeconds = 2.0;
  private readonly defaultMaxCollectionSeconds = 5.0;
  private readonly defaultMicDetectionRunDelaySeconds = 5;

  // At least one LSB 16-bit data (compare is on absolute value)
  readonly silentThreshold = 1.0 / 32767;
  readonly lowVolumeThreshold = -60;

  // Data must be identical within one LSB 16-bit to be identified as mono
  readonly monoDetectThreshold = 1.0 / 65536;

  // Number of consecutive clipThreshold level samples that indicate clipping
  readonly clipCountThreshold = 6;
  readonly clipThreshold = 1.0;

  // Audio data storage
  collectedAudio: Float32Array[][] = [];
  collectedSampleCount = 0;

  // Audio processing objects
  private audioContext: AudioContext | null = null;
  private audioSource: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private testCompleteResolve: ((results: MicMuteDetectionResult) => void) | null = null;

  constructor(private flagsService: FlagsService) {
    // Initialize the audio collection array
    for (let i = 0; i < this.inputChannelCount; ++i) {
      this.collectedAudio[i] = [];
    }
  }

  /**
   * Check if the AudioWorklet API is available and the feature flag is enabled
   * @returns true if mic test is available, false otherwise
   */
  isMicTestAvailable(): boolean {
    return (
      typeof AudioContext !== 'undefined' &&
      typeof AudioWorkletNode !== 'undefined' &&
      this.flagsService.isFlagEnabled(FLAGS.MIC_MUTE_DETECTION)
    );
  }

  /**
   * Get configuration values from feature flags
   */
  getMicDetectionConfig() {
    const micMuteDetectionConfig = this.flagsService.featureFlagsVariables?.mic_mute_detection;

    const maxAudioDataSeconds =
      (micMuteDetectionConfig?.max_audio_data_seconds as number) || this.defaultMaxAudioDataSeconds;
    const maxCollectionSeconds =
      (micMuteDetectionConfig?.max_collection_seconds as number) ||
      this.defaultMaxCollectionSeconds;
    const micDetectionRunDelaySeconds =
      (micMuteDetectionConfig?.mic_detection_run_delay_seconds as number) ||
      this.defaultMicDetectionRunDelaySeconds;

    return {
      maxAudioDataSeconds,
      maxCollectionSeconds,
      micDetectionRunDelaySeconds,
    };
  }

  /**
   * Run microphone test using an existing MediaStream
   * @param stream - The MediaStream from a microphone that should be analyzed
   * @returns Promise that resolves with test results or rejects with error
   */
  async runTest(stream: MediaStream): Promise<MicMuteDetectionResult> {
    // Check if mic test is available (including feature flag)
    if (!this.isMicTestAvailable()) {
      throw new Error(
        'Microphone test is not available. AudioWorklet API is not supported or feature flag is disabled.',
      );
    }

    // Reset the state for a new test
    this.resetState();

    // Check if the stream has audio tracks
    if (!this.checkAudioTracks(stream)) {
      throw new Error('No audio track in provided stream.');
    }

    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    await this.loadAudioWorkletModule();

    await this.setupAudioWorkletProcessing(stream);

    // Set up a promise that will resolve when audio analysis is complete
    const testCompletePromise = new Promise<MicMuteDetectionResult>((resolveTest) => {
      this.testCompleteResolve = resolveTest;
    });

    return testCompletePromise;
  }

  /**
   * Load the AudioWorklet processor module
   */
  private async loadAudioWorkletModule(): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    try {
      await this.audioContext.audioWorklet.addModule('assets/mic-mute-detection-worklet.js');
    } catch (error) {
      throw new Error(`Failed to load AudioWorklet module: ${error}`);
    }
  }

  /**
   * Check if the provided stream has audio tracks
   */
  private checkAudioTracks(stream: MediaStream): boolean {
    const audioTracks = stream.getAudioTracks();
    return audioTracks.length >= 1;
  }

  /**
   * Set up audio processing pipeline with AudioWorklet
   */
  private async setupAudioWorkletProcessing(stream: MediaStream): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    // Get configuration from flags
    const config = this.getMicDetectionConfig();

    this.audioSource = this.audioContext.createMediaStreamSource(stream);

    // Create AudioWorkletNode with configuration from flags
    this.workletNode = new AudioWorkletNode(this.audioContext, 'mic-test-processor', {
      processorOptions: {
        inputChannelCount: this.inputChannelCount,
        silentThreshold: this.silentThreshold,
        collectSeconds: config.maxAudioDataSeconds,
        maxCollectionSeconds: config.maxCollectionSeconds,
      },
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: this.inputChannelCount,
    });

    // Set up message handling from the worklet
    this.workletNode.port.onmessage = (event: WorkletMessageEvent) => {
      if (event.data.type === 'audioComplete') {
        this.onAudioCollectionComplete(event.data.collectedAudio, event.data.collectedSampleCount);
      }
    };

    // Connect the audio graph
    this.audioSource.connect(this.workletNode);

    // Set a backup timeout in case the worklet doesn't stop on its own
    modifiedSetTimeout(
      () => {
        if (this.workletNode) {
          const stopMessage: StopCommandMessage = {
            command: 'stop',
          };
          this.workletNode.port.postMessage(stopMessage);
        }
      },
      config.maxCollectionSeconds * 1000,
      true,
    );
  }

  /**
   * Handle completion of audio collection from the worklet
   */
  private onAudioCollectionComplete(
    collectedAudio: Float32Array[][],
    collectedSampleCount: number,
  ): void {
    this.collectedAudio = collectedAudio;
    this.collectedSampleCount = collectedSampleCount;

    // Disconnect audio nodes
    if (this.audioSource && this.workletNode) {
      this.audioSource.disconnect(this.workletNode);
    }

    // Analyze collected audio
    const results = this.analyzeAudio(this.collectedAudio);

    // Cleanup
    this.audioSource = null;
    this.workletNode = null;

    // Resolve the test promise with results
    if (this.testCompleteResolve) {
      this.testCompleteResolve(results);
      this.testCompleteResolve = null;
    }
  }

  /**
   * Analyze the collected audio data
   */
  private analyzeAudio(channels: Float32Array[][]): MicMuteDetectionResult {
    const results: MicMuteDetectionResult = {
      activeChannels: [],
      channelStats: [],
      isUnmuted: false,
      isClipping: false,
      hasLowVolume: false,
    };

    // Check each channel for activity
    for (let c = 0; c < channels.length; c++) {
      const stats = this.channelStats(c, channels[c]);
      if (stats.active) {
        results.activeChannels.push(c);
        results.channelStats.push(stats);

        if (stats.isClipping) {
          results.isClipping = true;
        }

        if (stats.hasLowVolume) {
          results.hasLowVolume = true;
        }
        results.isUnmuted = true;
      }
    }

    return results;
  }

  /**
   * Calculate statistics for a single channel
   */
  private channelStats(channelNumber: number, buffers: Float32Array[]): ChannelStats {
    let maxPeak = 0.0;
    let maxRms = 0.0;
    let clipCount = 0;
    let maxClipCount = 0;

    for (let j = 0; j < buffers.length; j++) {
      const samples = buffers[j];
      if (samples.length > 0) {
        let s = 0;
        let rms = 0.0;

        for (let i = 0; i < samples.length; i++) {
          s = Math.abs(samples[i]);
          maxPeak = Math.max(maxPeak, s);
          rms += s * s;

          if (maxPeak >= this.clipThreshold) {
            clipCount++;
            maxClipCount = Math.max(maxClipCount, clipCount);
          } else {
            clipCount = 0;
          }
        }

        // Calculate RMS for this buffer
        rms = Math.sqrt(rms / samples.length);
        maxRms = Math.max(maxRms, rms);
      }
    }

    const stats: ChannelStats = {
      channelNumber,
      active: maxPeak > this.silentThreshold,
      peakLevel: maxPeak,
      rmsLevel: maxRms,
      peakDb: this.dBFS(maxPeak),
      rmsDb: this.dBFS(maxRms),
      isClipping: maxClipCount > this.clipCountThreshold,
      hasLowVolume: this.dBFS(maxRms) < this.lowVolumeThreshold,
    };

    return stats;
  }

  /**
   * Convert gain to dBFS (decibels relative to full scale)
   */
  private dBFS(gain: number): number {
    if (gain <= 0) {
      return -Infinity;
    }
    const dB = (20 * Math.log(gain)) / Math.log(10);
    return Math.round(dB * 10) / 10;
  }

  /**
   * Reset the state for a new test
   */
  private resetState(): void {
    // Clear any previous audio data
    this.collectedAudio = [];
    this.collectedSampleCount = 0;

    // Initialize the audio collection array
    for (let i = 0; i < this.inputChannelCount; ++i) {
      this.collectedAudio[i] = [];
    }

    // Clear any previous audio processing
    if (this.audioSource && this.workletNode) {
      this.audioSource.disconnect(this.workletNode);
      this.audioSource = null;
      this.workletNode = null;
    }

    // Clear any pending test resolve function
    this.testCompleteResolve = null;
  }
}

/**
 * Interface for microphone test results
 */
export interface MicMuteDetectionResult {
  activeChannels: number[];
  channelStats: ChannelStats[];
  isUnmuted: boolean;
  isClipping: boolean;
  hasLowVolume: boolean;
}

/**
 * Interface for individual channel statistics
 */
export interface ChannelStats {
  channelNumber: number;
  active: boolean;
  peakLevel: number;
  rmsLevel: number;
  peakDb: number;
  rmsDb: number;
  isClipping: boolean;
  hasLowVolume: boolean;
}
